<template>
    <b-modal
        id="dischargeTreatment"
        ref="dischargeTreatment"
        :lazy="true"
        title-tag="h2"
        title="End Treatment"
    >
        <b-form>
            <LoaderCard
                v-if="loading"
                class="pl-3 pr-3 pb-2"
                :customWidth="100"
                :customHeight="2"
            ></LoaderCard>
            <div v-if="!hasAppointmentToLink && !loading">
                <div
                    v-if="
                        data.status === TREATMENT_STATUS.DISCHARGED &&
                        data.appointment_id !== null
                    "
                >
                    Ending treatment will remove this patient from the treatment
                    board and set the related appointment status to "Visit
                    Complete."
                </div>
                <div
                    v-else-if="
                        data.status === TREATMENT_STATUS.CANCELLED &&
                        data.appointment_id !== null
                    "
                >
                    One or more activities have not been completed yet. Ending
                    treatment early will delete these activities, and no medical
                    records or charges will be created. In addition, this
                    patient will be removed from the treatment board and the
                    appointment status will be set to "Visit Complete."
                </div>
                <div
                    v-else-if="
                        data.status === TREATMENT_STATUS.DISCHARGED &&
                        data.appointment_id === null
                    "
                >
                    Ending treatment will remove this patient from the treatment
                    board and create an appointment at the discharge time
                    specified below with a status of "Visit Complete."
                </div>
                <div
                    v-else-if="
                        data.status === TREATMENT_STATUS.CANCELLED &&
                        data.appointment_id === null
                    "
                >
                    One or more activities have not been completed yet. Ending
                    treatment early will delete these activities, and no medical
                    records or charges will be created. In addition, this
                    patient will be removed from the treatment board and an
                    appointment will be created at the discharge time specified
                    below with a status of "Visit Complete."
                </div>
                <hr />
            </div>
            <div v-else>
                <div v-if="appointmentToLinkData !== null && !loading">
                    <p>
                        One or more activities have not been completed yet.
                        Ending treatment early will delete these activities, and
                        no medical records or charges will be created. In
                        addition, this patient will be removed from the
                        treatment board.
                    </p>
                    <p>
                        Patient
                        <strong
                            class="text-primary cursor-pointer"
                            @click="openLinkNewTab('patient', data)"
                            target="_blank"
                            >{{ data.patient.name }}</strong
                        >
                        has an appointment that is not Checked Out dated at
                        <strong
                            class="text-primary cursor-pointer"
                            @click="
                                openLinkNewTab(
                                    'appointment',
                                    appointmentToLinkData
                                )
                            "
                            target="_blank"
                        >
                            {{
                                _setDateTimeDisplay(appointmentToLinkData.start)
                            }} </strong
                        >. You can choose to associate this treatment with the
                        appointment.
                    </p>
                    <p>
                        If you choose not to associate this treatment with the
                        appointment, an appointment will be created at the
                        discharge time specified below with a status of "Visit
                        Complete."
                    </p>
                    <div>
                        <b-form-checkbox
                            name="associateWithAppointment"
                            class="col-form-label"
                            plain
                            v-model="associateWithAppointment"
                        >
                            <span class="fw-normal"
                                >Associate treatment with appointment on
                                <strong
                                    class="text-primary cursor-pointer"
                                    @click="
                                        openLinkNewTab(
                                            'appointment',
                                            appointmentToLinkData
                                        )
                                    "
                                    target="_blank"
                                >
                                    {{
                                        _setDateTimeDisplay(
                                            appointmentToLinkData.start
                                        )
                                    }}
                                </strong></span
                            >
                        </b-form-checkbox>
                    </div>
                    <hr />
                    <p
                        v-if="
                            appointmentToLinkData.soap_id !== null &&
                            data.soap_id !== null &&
                            associateWithAppointment
                        "
                    >
                        A medical note is already associated with the existing
                        appointment. The medical note
                        <strong
                            class="text-primary cursor-pointer"
                            @click="
                                openLinkNewTab(
                                    'soap',
                                    appointmentToLinkData.soap
                                )
                            "
                            target="_blank"
                        >
                            {{
                                appointmentToLinkData &&
                                appointmentToLinkData.soap &&
                                appointmentToLinkData.soap.label
                            }}
                        </strong>
                        will be dissociated from the appointment, and the
                        medical note
                        <strong
                            class="text-primary cursor-pointer"
                            @click="openLinkNewTab('soap', data.active_soap)"
                            target="_blank"
                        >
                            {{ data.active_soap ? data.active_soap.label : "" }}
                        </strong>
                        that is associated with the treatment will be
                        associated.
                    </p>
                    <p v-if="hasInvoiceToLink && associateWithAppointment">
                        <strong
                            class="text-primary cursor-pointer"
                            @click="
                                openLinkNewTab(
                                    billingAppointment.module,
                                    billingAppointment
                                )
                            "
                            target="_blank"
                        >
                            {{ billingAppointment.label }}
                        </strong>
                        was created during check-in and linked to the
                        appointment. By doing this, for data linking purposes,
                        <strong
                            class="text-primary cursor-pointer"
                            @click="
                                openLinkNewTab(
                                    billingTreatment.module,
                                    billingTreatment
                                )
                            "
                            target="_blank"
                        >
                            {{ billingTreatment.label }}
                        </strong>
                        will be linked to the appointment in place of
                        <strong
                            class="text-primary cursor-pointer"
                            s
                            @click="
                                openLinkNewTab(
                                    billingAppointment.module,
                                    billingAppointment
                                )
                            "
                            target="_blank"
                        >
                            {{ billingAppointment.label }} </strong
                        >. This may impact sales reports.
                    </p>
                </div>
            </div>
            <b-form-group
                :label-for="`end`"
                :label-cols="labelCols"
                label="Discharge At:"
            >
                <vet-datetime
                    id="end"
                    v-model="data.end"
                    v-validate="'required'"
                    :min-time="data.start"
                    :placeholder="{
                        date: 'Date (required)',
                        time: 'Time (required)'
                    }"
                    container="#dischargeTreatment"
                    rules="required"
                    :step="_setting.slot_minutes"
                    name="end"
                ></vet-datetime>
            </b-form-group>
            <b-form-group
                v-if="!data.appointment_id && !associateWithAppointment"
                :label-for="`provider`"
                :label-cols="labelCols"
                label="Assign To:"
            >
                <b-form-select
                    id="staff_id"
                    v-model="data.staff"
                    v-validate="'required'"
                    :disabled="!_.some(appointmentTypes)"
                    class="form-control"
                    name="staff_id"
                    :options="staffList"
                >
                </b-form-select>
            </b-form-group>
            <b-form-group
                v-if="!data.appointment_id"
                :label-for="`provider`"
                :label-cols="labelCols"
                label="Provider:"
            >
                <b-form-select
                    id="provide_id"
                    v-model="data.provider"
                    v-validate="'required'"
                    :disabled="!_.some(appointmentTypes)"
                    class="form-control"
                    name="provide_id"
                    :options="providerList"
                >
                </b-form-select>
            </b-form-group>
            <b-form-group
                v-if="!data.appointment_id && !associateWithAppointment"
                :label-for="`appointment_type`"
                :label-cols="labelCols"
                label="Appointment Type:"
            >
                <b-form-select
                    id="appointment_type"
                    v-model="data.appointment_type"
                    v-validate="'required'"
                    :disabled="!_.some(appointmentTypes)"
                    class="form-control"
                    name="appointment_type"
                    :options="appointmentTypes"
                >
                </b-form-select>
            </b-form-group>
            <b-form-group
                :label-for="`notes`"
                :label-cols="labelCols"
                label="Notes:"
            >
                <b-form-textarea
                    id="notes"
                    name="notes"
                    v-model="data.note"
                    class="form-control"
                    placeholder="Notes (optional)"
                    rows="3"
                ></b-form-textarea>
            </b-form-group>
        </b-form>
        <div slot="modal-footer" class="w-100">
            <b-button
                v-if="!loading"
                @click="$refs.dischargeTreatment.hide('cancel')"
                class="float-left"
                variant="secondary"
                >Cancel</b-button
            >
            <div class="float-right">
                <img v-if="loading" src="assets/ajax-loader.gif" />
                <b-button
                    v-if="!loading"
                    @click="save"
                    class="float-left"
                    variant="primary"
                    >End</b-button
                >
            </div>
        </div>
    </b-modal>
</template>
<style></style>
<script>
import ScheduleHelper from "../helper";
import ScheduleServices from "@/services/schedule";
import vetDatetime from "@/module/commons/date-time-picker.vue";
import { concat, assign, find, map, filter, get } from "lodash";
import { DateTime } from "luxon";
import CensusMixin from "@/mixins/census";
import LoaderCard from "@/components/elements/loader-card";

export default {
    name: "dischargeTreatment",
    components: {
        vetDatetime,
        LoaderCard
    },
    props: {
        instanceId: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            loading: false,
            data: {
                end: null,
                start: null,
                id: null,
                status: null,
                patient_id: null,
                appointment_id: null,
                appointment_type_id: null,
                staff: null,
                provider: null,
                note: ""
            },
            rooms: [],
            staffList: [],
            providerList: [],
            appointmentTypes: [],
            hasAppointmentToLink: false,
            appointmentToLinkData: null,
            associateWithAppointment: false,
            hasInvoiceToLink: false,
            billingAppointment: [],
            billingTreatment: []
        };
    },
    mixins: [ScheduleHelper, CensusMixin],
    watch: {
        "data.end": function (newValue, oldValue) {
            if (!newValue || !oldValue) return;

            if (
                Math.round(newValue.toSeconds()) !==
                Math.round(oldValue.toSeconds())
            ) {
                this.hasAppointmentToLink = false;
                this.appointmentToLinkData = null;
                this.associateWithAppointment = false;
                this.hasInvoiceToLink = false;
                this.billingAppointment = [];
                this.billingTreatment = [];
                this.getAppointmentToLink();
            }
        }
    },
    computed: {
        labelCols() {
            return this.data.appointment_id ? 3 : 4;
        }
    },
    methods: {
        open(params) {
            this.loading = true;
            this.getAppointmentType();
            this.getStaffList();
            this.getProviders();
            this.data = assign(this.data, params);
            this.data.patient_id = params.patient.id;
            this.data.end = DateTime.local().setZone(this.timeZone);
            this.data.start = this._toDateTime(params.start);
            if (this.data.appointment_id) {
                this.$refs["dischargeTreatment"].show();
                this.loading = false;
            } else {
                this.getAppointmentToLink(params);
            }
        },
        getAppointmentToLink() {
            this.$refs["dischargeTreatment"].show();
            ScheduleServices.getAppointmentsForTreatmentBoard(
                this.data.patient.id,
                this.data.end.toSeconds()
            ).then(response => {
                if (response.code == 200) {
                    if (response.resource) {
                        this.hasAppointmentToLink = true;
                        this.appointmentToLinkData = response.resource;

                        // check for billingAppointment
                        // 1. check if there soap_id and soap_billing in this.appointmentToLinkData, if yes, then hasInvoiceToLink = true
                        // 2. check if there invoice_id in this.appointmentToLinkData, if yes, then hasInvoiceToLink = true
                        // 3. check if there estimate_id in this.appointmentToLinkData, if yes, then hasInvoiceToLink = true

                        if (
                            this.appointmentToLinkData.soap_id &&
                            this.appointmentToLinkData.soap_billing?.length > 0
                        ) {
                            this.billingAppointment = {
                                module: "soap",
                                encoded_id: this.appointmentToLinkData.soap_id,
                                label: this.appointmentToLinkData.soap_billing
                                    .name
                            };
                        }

                        if (
                            this.appointmentToLinkData.invoice_id &&
                            this.appointmentToLinkData.invoice
                        ) {
                            this.hasInvoiceToLink = true;
                            this.billingAppointment = {
                                module: "invoice",
                                encoded_id: this.appointmentToLinkData.invoice
                                    .encoded_id,
                                label: this.appointmentToLinkData.invoice.name
                            };
                        } else if (
                            this.appointmentToLinkData.estimate_id &&
                            this.appointmentToLinkData.estimate
                        ) {
                            this.hasInvoiceToLink = true;
                            this.billingAppointment = {
                                module: "estimate",
                                encoded_id: this.appointmentToLinkData.estimate
                                    .encoded_id,
                                label: this.appointmentToLinkData.estimate.name
                            };
                        }

                        // check for billingTreatment
                        if (this.hasInvoiceToLink) {
                            if (this.data.invoice_id && this.data.invoice) {
                                this.billingTreatment = {
                                    module: "invoice",
                                    encoded_id: this.data.invoice.encoded_id,
                                    label: this.data.invoice.name
                                };
                            } else if (
                                this.data.estimate_id &&
                                this.data.estimate
                            ) {
                                this.billingTreatment = {
                                    module: "estimate",
                                    encoded_id: this.data.estimate.encoded_id,
                                    label: this.data.estimate.name
                                };
                            }
                        }
                    }
                }
                this.loading = false;
            });
        },
        getAppointmentType() {
            let self = this;
            return this.$http
                .get(process.env.SCHEDULE_SERVICE + "appointment-type")
                .then(response => {
                    self.appointmentTypes = response.data.response.resources.map(
                        types => ({
                            value: types.id,
                            data: types,
                            isdefault: types.isdefault,
                            text: types.type //+ " " + types.duration + " minutes"
                        })
                    );
                    self.data.appointment_type = find(self.appointmentTypes, {
                        isdefault: true
                    }).value;
                });
        },
        getProviders() {
            let self = this;
            return this.$http
                .get(process.env.BARRAMUNDI + "actor/staff/business/providers")
                .then(function (response) {
                    self.providerList = get(
                        response,
                        "data.response.resource.data",
                        []
                    );

                    self.providerList = _.map(self.providerList, element => ({
                        value: element.actor.id,
                        text:
                            element.actor.firstname +
                            " " +
                            element.actor.lastname,
                        is_default_provider: element.is_default_provider
                    }));

                    // --- sort by provider name
                    self.providerList = _.sortBy(self.providerList, [
                        "providerName"
                    ]);

                    let defaultProvider = find(
                        self.providerList,
                        "is_default_provider"
                    );
                    let currentStaff = find(self.providerList, [
                        "value",
                        self.$root.currentActor.id
                    ]);

                    if (currentStaff) {
                        self.data.provider = currentStaff.value;
                    } else if (defaultProvider) {
                        self.data.provider = defaultProvider.value;
                    } else {
                        self.data.provider = self.providerList[0].value;
                    }
                });
        },
        getStaffList() {
            let self = this;
            return this.$http
                .get(process.env.BARRACUDA + "account/schedule-columns")
                .then(function (response) {
                    if (response.data.response.resource.data) {
                        // schedule column staff
                        self.staffList = map(
                            filter(response.data.response.resource.data, {
                                type: 1
                            }),
                            staff => ({
                                value: staff.staff.id,
                                text:
                                    staff.staff.firstname +
                                    " " +
                                    staff.staff.lastname,
                                data: staff.staff
                            })
                        );
                        // schedule column non-staff && merge
                        self.staffList = concat(
                            self.staffList,
                            map(
                                filter(response.data.response.resource.data, {
                                    type: 0
                                }),
                                staff => ({
                                    value: staff.id,
                                    text: staff.display_name,
                                    data: staff
                                })
                            )
                        );

                        // --- sort by provider name
                        self.staffList = _.sortBy(self.staffList, ["text"]);
                        // -- set default staff
                        let currentStaff = "";
                        if (self.staffList.length > 0) {
                            if (
                                (currentStaff = find(self.staffList, [
                                    "value",
                                    self.$root.currentActor.id
                                ]))
                            ) {
                                self.data.staff = currentStaff.value;
                            } else {
                                self.data.staff = self.staffList[0].value;
                            }
                        }
                    }
                    return self.staffList;
                });
        },
        async save() {
            let response = false;
            let valid = await this.$validator.validateAll();

            if (valid) {
                let params = {
                    appointment_id: this.associateWithAppointment
                        ? this.appointmentToLinkData.id
                        : null,
                    link_exist_appointment: this.associateWithAppointment
                        ? true
                        : false,
                    note: this.data.note,
                    status_id: this.TREATMENT_STATUS.DISCHARGED,
                    patient_id: this.data.patient_id,
                    end: this.data.end.toSeconds(),
                    triage_type_id: this.data?.active_triage?.triage_type_id
                };

                try {
                    this.loading = true;
                    let appointment = null;

                    // if having appointment, then change status to something else.
                    if (!this.associateWithAppointment) {
                        if (this.data.appointment_id) {
                            let appointment_ref = await this._getAppointment(
                                this.data.appointment_id
                            );
                            // -- create proxy appointment when diff day
                            if (
                                appointment_ref &&
                                this.data.end
                                    .setZone(this._timeZone)
                                    .startOf("day") >
                                    this._toDateTime(appointment_ref.start)
                                        .setZone(this._timeZone)
                                        .startOf("day")
                            ) {
                                appointment = await this.$http.post(
                                    process.env.BARRACUDA +
                                        "schedule/appointment",
                                    {
                                        note: this.data.note,
                                        occurrence_id: appointment_ref.id,
                                        opt_out_recurring: true,
                                        start: Math.round(
                                            this.data.end.toSeconds()
                                        ),
                                        end: Math.round(
                                            this.data.end
                                                .plus({ minutes: 15 })
                                                .toSeconds()
                                        ),
                                        status: 3,
                                        staff_id: appointment_ref.staff_id,
                                        provider_id: appointment_ref.provider_id
                                    }
                                );
                            }
                            appointment = await this.$http.post(
                                process.env.BARRACUDA +
                                    "schedule/appointment/" +
                                    this.data.appointment_id +
                                    "/status",
                                {
                                    status_id: this.APPOINTMENT
                                        .STATUS_VISIT_COMPLETE,
                                    note: this.data.note
                                }
                            );
                        } else {
                            let appointmentType = find(this.appointmentTypes, {
                                value: this.data.appointment_type
                            });

                            appointment = await this.$http.post(
                                process.env.BARRACUDA + "schedule/appointment",
                                {
                                    appointment_type: this.data
                                        .appointment_type,
                                    status_id: this.APPOINTMENT
                                        .STATUS_VISIT_COMPLETE,
                                    note: this.data.note,
                                    reason: this.data.admitted_for,
                                    start: Math.round(
                                        this.data.end.toSeconds()
                                    ),
                                    end: Math.round(
                                        this.data.end
                                            .plus({
                                                minutes:
                                                    appointmentType.data
                                                        .duration
                                            })
                                            .toSeconds()
                                    ),
                                    staff_id: this.data.staff,
                                    provider_id: this.data.provider,
                                    patient_id: this.data.patient_id,
                                    client_id: this.data.client_id,
                                    invoice_id: this.data.invoice_id,
                                    estimate_id: this.data.estimate_id,
                                    soap_id: this.data.soap_id,
                                    location_id: null,
                                    status: 1,
                                    repeat: 1,
                                    stop: 0
                                }
                            );
                            params.appointment_id =
                                appointment.data.response.resources.id;
                        }
                    }

                    this.$socket.client.emit("v-broadcast-all", {
                        action: "refresh",
                        businessId: this._setting.business_id,
                        instance: this.uniqid(),
                        params: !this.associateWithAppointment
                            ? appointment.data.response.resources
                            : this.appointmentToLinkData
                    });

                    response = await this.$http.put(
                        process.env.BARRACUDA +
                            "schedule/treatment-board/" +
                            this.data.id,
                        params
                    );

                    if (response.status === 200) {
                        // handle treatment summary
                        this.$http
                            .get(
                                process.env.BARRAMUNDI +
                                    "schedule/treatment-summary-discharge/" +
                                    this.data.id
                            )
                            .then(function (response) {});

                        this.loading = false;
                        this.$refs.dischargeTreatment.hide();
                        this.$root.$emit("treatment-board-refresh", {
                            params: response.data.response
                        });
                        this._message("Treatment discharged", "success");

                        // Emit socket to refresh census
                        const payload = {
                            appointmentDate: this.data?.start.toSeconds(),
                            appointmentId: this.data?.appointment_id,
                            instanceId: this.instanceId
                        };

                        this.refreshCensus(payload);
                    } else {
                        this.loading = false;
                        this._message("Error saving data", "danger");
                    }
                } catch (errors) {
                    this.loading = false;
                    console.error(errors);
                    this._message("Error saving data", "danger");
                }
            }
        },
        openLinkNewTab(module, data) {
            // check null data
            if (data === null) {
                return;
            }
            switch (module) {
                case "patient":
                    window.open(
                        this.$router.resolve({
                            name: "patient_profile",
                            params: {
                                id: data.patient.encoded_id
                            }
                        }).href,
                        "_blank"
                    );
                    break;
                case "invoice":
                    window.open(
                        this.$router.resolve({
                            name: "invoice",
                            params: {
                                id: data.encoded_id
                            }
                        }).href,
                        "_blank"
                    );
                    break;
                case "estimate":
                    window.open(
                        this.$router.resolve({
                            name: "estimate",
                            params: {
                                id: data.estimate_id,
                                view: "view"
                            }
                        }).href,
                        "_blank"
                    );
                    break;
                case "appointment":
                    window.open(
                        this.$router.resolve({
                            name: "appointment",
                            params: {
                                id: data.id,
                                start: data.sort_date,
                                view: "resourceTimeGridDay"
                            }
                        }).href,
                        "_blank"
                    );
                    break;
                case "soap":
                    window.open(
                        this.$router.resolve({
                            name: "soap",
                            params: {
                                id: data.encoded_id
                            }
                        }).href,
                        "_blank"
                    );
                    break;
            }
        }
    }
};
</script>
